import { FormGroup } from '@angular/forms';
import { WorkflowValidationType } from '../models/workflows.models';
import { resolveSelectIdOrPlainValue } from './workflow-form-control.util';
import {
	mapValidationApiTypeToManualTypes,
	mapValidationManualTypesToApi,
	WorkflowValidationManualTypeId,
} from './workflow-validation-manual-type.util';

export function parseValidationTypeFromDetail(validationType: unknown): {
	mode: 'auto' | 'manual';
	manualType: WorkflowValidationManualTypeId[];
	manualErrors: string[];
} {
	const defaultManualType: WorkflowValidationManualTypeId[] = ['approved'];

	// Contrato API: `{ type: 'auto'|'ok'|'ko'|'ok-ko', errors: string[] }`
	if (typeof validationType === 'object' && validationType) {
		const rawType = (validationType as { type?: unknown }).type;
		const rawErrors = (validationType as { errors?: unknown }).errors;
		const errorsArray = Array.isArray(rawErrors) ? rawErrors.map((e) => String(e)) : [];

		return mapProcessTypeToFormState(rawType, errorsArray, defaultManualType);
	}

	// Defensa: GET a veces aplana el tipo (`"type": "ok"` en lugar del objeto).
	if (typeof validationType === 'string') {
		return mapProcessTypeToFormState(validationType, [], defaultManualType);
	}

	return { mode: 'auto', manualType: defaultManualType, manualErrors: [] };
}

function mapProcessTypeToFormState(
	rawType: unknown,
	manualErrors: string[],
	defaultManualType: WorkflowValidationManualTypeId[],
): {
	mode: 'auto' | 'manual';
	manualType: WorkflowValidationManualTypeId[];
	manualErrors: string[];
} {
	if (rawType === 'auto' || rawType == null || rawType === '') {
		return { mode: 'auto', manualType: defaultManualType, manualErrors: [] };
	}

	const normalized =
		rawType === 'ok_ko' ? 'ok-ko' : rawType === 'ok' || rawType === 'ko' || rawType === 'ok-ko' ? rawType : null;

	if (!normalized) {
		return { mode: 'auto', manualType: defaultManualType, manualErrors: [] };
	}

	return {
		mode: 'manual',
		manualType: mapValidationApiTypeToManualTypes(normalized),
		// `errors: []` en API = todos los errores seleccionados en UI.
		manualErrors,
	};
}

export function normalizeValidationManualErrorIds(value: unknown): string[] {
	if (!Array.isArray(value)) {
		return [];
	}
	return value
		.map((item) =>
			typeof item === 'object' && item && 'id' in item ? String((item as { id: unknown }).id) : String(item),
		)
		.filter((id) => id.length > 0 && id !== 'all_errors');
}

export function areAllValidationErrorsSelected(selectedIds: string[], allOptionIds: string[]): boolean {
	if (allOptionIds.length === 0) {
		return true;
	}
	return allOptionIds.every((id) => selectedIds.includes(id));
}

export function buildWorkflowValidationTypePayload(
	form: FormGroup,
	companyManualValidation: boolean | undefined,
	allErrorOptionIds: string[] = [],
): WorkflowValidationType {
	const canManual = companyManualValidation === true;
	const mode = canManual
		? resolveSelectIdOrPlainValue<'auto' | 'manual'>(form.get('general.validation')?.value, 'auto')
		: 'auto';

	if (mode === 'auto') {
		return { type: 'auto', errors: [] };
	}

	const processValidationType = mapValidationManualTypesToApi(
		form.get('general.validation_manual_type')?.value,
	);

	const selectedErrorIds = normalizeValidationManualErrorIds(form.get('general.validation_manual_errors')?.value);
	// Vacío o todos seleccionados → `errors: []` (mismo contrato de “todos”).
	const errorsPayload =
		selectedErrorIds.length === 0 || areAllValidationErrorsSelected(selectedErrorIds, allErrorOptionIds)
			? []
			: selectedErrorIds;

	return processValidationType === 'ok'
		? { type: processValidationType, errors: [] }
		: { type: processValidationType, errors: errorsPayload };
}

export function buildWorkflowValidationEmailsPayload(
	form: FormGroup,
	companyManualValidation: boolean | undefined,
): string {
	if (companyManualValidation !== true) {
		return '';
	}

	const mode = resolveSelectIdOrPlainValue<'auto' | 'manual'>(form.get('general.validation')?.value, 'auto');
	return mode === 'manual' ? String(form.get('general.validation_email')?.value ?? '') : '';
}
